/**
 * [Gurumi 에디터 UI]
 * 역할: 툴바 아이콘 SVG (undo/redo/image는 참고 UI와 동일 스타일)
 * 작성/수정일: 2026-07-29
 */
const NS = 'http://www.w3.org/2000/svg';

/**
 * 아이콘 도형 정의
 * - string: 단일 path d
 * - array: { tag: 'path'|'circle'|'rect', ...attrs }
 */
const ICON_SHAPES = {
  // [툴바 아이콘] 역할: 이전(undo) — 반원 곡선 + 왼쪽 화살표 — 작성/수정일: 2026-07-29
  undo: [
    { tag: 'path', d: 'M9 14 4 9l5-5' },
    { tag: 'path', d: 'M4 9h10.5a5.5 5.5 0 0 1 5.5 5.5v0a5.5 5.5 0 0 1-5.5 5.5H11' },
  ],
  // [툴바 아이콘] 역할: 다음(redo) — 반원 곡선 + 오른쪽 화살표 — 작성/수정일: 2026-07-29
  redo: [
    { tag: 'path', d: 'm15 14 5-5-5-5' },
    { tag: 'path', d: 'M20 9H9.5A5.5 5.5 0 0 0 4 14.5v0A5.5 5.5 0 0 0 9.5 20H13' },
  ],
  bold: 'M6 4h8a4 4 0 0 1 4 4 4 4 0 0 1-4 4H6z M6 12h9a4 4 0 0 1 4 4 4 4 0 0 1-4 4H6z',
  italic: 'M19 4h-9 M14 20H5 M15 4 9 20',
  link: 'M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71 M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71',
  unlink: 'M18.84 12.25 21.17 9.92a5 5 0 0 0-7.07-7.07l-2.83 2.83 M5.17 14.75 2.83 17.08a5 5 0 0 0 7.07 7.07l2.83-2.83 M8.59 8.59 15.41 15.41',
  // [툴바 아이콘] 역할: 이미지+ — 프레임·산·원·우상단 plus — 작성/수정일: 2026-07-29
  image: [
    { tag: 'path', d: 'M16 5h6' },
    { tag: 'path', d: 'M19 2v6' },
    { tag: 'path', d: 'M21 11.5V19a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h7.5' },
    { tag: 'path', d: 'm21 15-3.086-3.086a2 2 0 0 0-2.828 0L6 21' },
    { tag: 'circle', cx: '9', cy: '9', r: '2' },
  ],
  video: 'm16 13 5.223 3.482a.5.5 0 0 0 .777-.416V7.87a.5.5 0 0 0-.752-.432L16 10.5 M14.5 6h-8A2.5 2.5 0 0 0 4 8.5v7A2.5 2.5 0 0 0 6.5 18h8a2.5 2.5 0 0 0 2.5-2.5v-7A2.5 2.5 0 0 0 14.5 6',
  alignLeft: 'M21 6H3 M15 12H3 M17 18H3',
  alignCenter: 'M21 6H3 M17 12H7 M19 18H5',
  alignRight: 'M21 6H3 M21 12H9 M21 18H11',
  chevronDown: 'm6 9 6 6 6-6',
  check: 'M20 6 9 17l-5-5',
  search: 'm21 21-4.34-4.34 M11 19a8 8 0 1 0 0-16 8 8 0 0 0 0 16',
  loader: 'M12 2v4 M12 18v4 M4.93 4.93l2.83 2.83 M16.24 16.24l2.83 2.83 M2 12h4 M18 12h4 M4.93 19.07l2.83-2.83 M16.24 7.76l2.83-2.83',
  x: 'M18 6 6 18 M6 6l12 12',
};

/**
 * [툴바 아이콘]
 * - 역할: 글자색·강조색 — 라운드 단색 칩
 * - 작성/수정일: 2026-08-12
 * @param {string} [activeColor]
 * @returns {SVGElement}
 */
export function paletteIconSvg(activeColor) {
  const fill = activeColor || '#37352F';
  const svg = document.createElementNS(NS, 'svg');
  svg.setAttribute('class', 'g5-gurumi-icon g5-gurumi-palette-icon');
  svg.setAttribute('width', '16');
  svg.setAttribute('height', '16');
  svg.setAttribute('viewBox', '0 0 24 24');
  svg.setAttribute('aria-hidden', 'true');

  const chip = document.createElementNS(NS, 'rect');
  chip.setAttribute('x', '3.5');
  chip.setAttribute('y', '3.5');
  chip.setAttribute('width', '17');
  chip.setAttribute('height', '17');
  chip.setAttribute('rx', '5.5');
  chip.setAttribute('ry', '5.5');
  chip.setAttribute('fill', fill);
  chip.setAttribute('stroke', 'rgb(15 15 15 / 0.14)');
  chip.setAttribute('stroke-width', '1');
  svg.appendChild(chip);

  return svg;
}

function appendShape(svg, shape) {
  if (typeof shape === 'string') {
    const path = document.createElementNS(NS, 'path');
    path.setAttribute('d', shape);
    svg.appendChild(path);
    return;
  }

  const el = document.createElementNS(NS, shape.tag || 'path');
  Object.keys(shape).forEach((key) => {
    if (key === 'tag') return;
    el.setAttribute(key, String(shape[key]));
  });
  svg.appendChild(el);
}

export function iconSvg(name, className = 'g5-gurumi-icon') {
  const svg = document.createElementNS(NS, 'svg');
  svg.setAttribute('class', className);
  svg.setAttribute('width', '16');
  svg.setAttribute('height', '16');
  svg.setAttribute('viewBox', '0 0 24 24');
  svg.setAttribute('fill', 'none');
  svg.setAttribute('stroke', 'currentColor');
  svg.setAttribute('stroke-width', '2');
  svg.setAttribute('stroke-linecap', 'round');
  svg.setAttribute('stroke-linejoin', 'round');
  svg.setAttribute('aria-hidden', 'true');

  const shape = ICON_SHAPES[name];
  if (Array.isArray(shape)) {
    shape.forEach((part) => appendShape(svg, part));
  } else if (shape) {
    appendShape(svg, shape);
  }

  if (name === 'loader') {
    svg.classList.add('g5-gurumi-spin');
  }

  return svg;
}
